import { Component, Inject, OnInit } from '@angular/core';
import { IAccount, IVisibleUsers } from 'app/store/models/account.model';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { TABLE_COMPONENT, TableComponent } from '../../../shared/ui/table/table.component';
import { FranchiseeService } from 'app/franchisee/franchisee.service';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { AccountsUnderComponent } from '../accounts-under/accounts-under.component';
import { FranchiseeSelectDialogComponent } from '../franchisee-select-dialog/franchisee-select-dialog.component';
import { DialogComponent } from 'app/shared/ui/dialog/dialog.component';
import { UserType } from 'app/store/models/user.model';

@Component({
  selector: 'app-franchisee-control',
  templateUrl: './franchisee-control.component.html',
  styleUrls: ['./franchisee-control.component.scss'],
})
export class FranchiseeControlComponent implements OnInit {
  public column: IAccount;
  public franchisees: IAccount[];

  viewRepButtonProperties: Button = {
    text: 'View reps',
    color: ButtonColor.ORANGE,
    size: ButtonSize.NORMAL,
    style: 'rounded',
    onClick: () => this.openViewRepsDialog(),
  };

  attributeButtonProperties: Button = {
    text: 'Add Franchisee',
    color: ButtonColor.ORANGE,
    size: ButtonSize.NORMAL,
    style: 'rounded',
    onClick: () => this.openAttributeDialog(),
  };

  constructor(
    private store: Store<IReducers>,
    private franchiseeService: FranchiseeService,
    @Inject(TABLE_COMPONENT) private tableComponent: TableComponent,
  ) {}

  ngOnInit() {
    if (this.isInherited) {
      this.attributeButtonProperties.text = `Inherit from ${this.column.inherit_from_id}`;
      this.attributeButtonProperties.onClick = () => undefined;
    } else {
      this.attributeButtonProperties.text = this.column.franchisee_id
        ? 'Change Franchisee'
        : 'Add Franchisee';
    }
  }

  get isInherited(): boolean {
    return this.column.inherit_from_id != null;
  }

  get canAttribute(): boolean {
    return (
      this.column.account_role !== UserType.FRANCHISE &&
      this.column.account_role !== UserType.VSM &&
      this.column.account_role !== UserType.MASTER
    );
  }

  get canChangeFranchisee(): boolean {
    return this.canAttribute && !this.isInherited;
  }

  getFranchiseeLabel(): string {
    if (!this.column.franchisee_id) {
      return '';
    }
    const franchisee = this.findFranchisee(this.column.franchisee_id);
    const email = franchisee?.email || `Franchisee #${this.column.franchisee_id}`;
    if (this.column.referral_partner_name) {
      return `${email} / ${this.column.referral_partner_name}`;
    }
    return email;
  }

  resetFranchisee() {
    if (this.isInherited) {
      return;
    }
    const currentFranchisee = this.findFranchisee(this.column.franchisee_id);
    if (!currentFranchisee) {
      return;
    }

    this.removeUserFromFranchisee(currentFranchisee, accountIds => {
      this.syncFranchiseeVisibleUsers(currentFranchisee, accountIds);
      this.column.franchisee_id = null;
      this.column.referral_partner_id = null;
      this.column.referral_partner_name = null;
      this.handleSuccess();
    });
  }

  openViewRepsDialog() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: `User Account(s) under ${this.column.first_name} ${this.column.last_name}`,
          content: AccountsUnderComponent,
          componentData: {
            items: this.column.visible_users,
            account: this.column,
          },
          dialogMinWidth: '1000px',
          canClose: true,
        },
      }),
    );
  }

  openAttributeDialog() {
    if (this.isInherited) {
      return;
    }
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: this.column.franchisee_id ? 'Change Franchisee' : 'Select Franchisee',
          content: FranchiseeSelectDialogComponent,
          componentData: {
            user: this.column,
            franchisees: this.franchisees,
          },
          canClose: true,
          dialogMinWidth: '600px',
          buttons: [
            {
              text: 'Save',
              color: ButtonColor.BLUE,
              size: ButtonSize.NORMAL,
              onClick: (dialog: DialogComponent) => {
                if (!dialog.dialogForm.valid) {
                  dialog.dialogForm.markAllAsTouched();
                  dialog.releaseButtons();
                  return;
                }
                dialog.lockButtons();
                this.saveAttribution(dialog);
              },
            },
          ],
        },
      }),
    );
  }

  private saveAttribution(dialog: DialogComponent, confirmTransfer = false) {
    const data = dialog.dialogForm.value;
    const franchiseeId = Number(data.franchisee_id);
    const franchisee = this.findFranchisee(franchiseeId);
    if (!franchisee) {
      dialog.releaseButtons();
      return;
    }

    const referralPartnerId =
      data.referral_partner_id === '' || data.referral_partner_id == null
        ? null
        : Number(data.referral_partner_id);

    const previousFranchiseeId = this.column.franchisee_id
      ? Number(this.column.franchisee_id)
      : null;
    const hasPartnerLink = !!this.column.referral_partner_id;
    const changingFranchisee =
      previousFranchiseeId != null && previousFranchiseeId !== franchiseeId;

    // Cross-franchisee without partner link: remove old attribution first (API blocks otherwise).
    if (changingFranchisee && !hasPartnerLink && !confirmTransfer) {
      const previousFranchisee = this.findFranchisee(previousFranchiseeId);
      if (previousFranchisee) {
        this.franchiseeService
          .removeFranchiseeAssignment(previousFranchisee.account_id, Number(this.column.account_id))
          .subscribe(
            () => this.assignWithPartner(franchisee, referralPartnerId, dialog, false),
            error => this.handleDialogError(dialog, error),
          );
        return;
      }
    }

    this.assignWithPartner(franchisee, referralPartnerId, dialog, confirmTransfer);
  }

  private assignWithPartner(
    franchisee: IAccount,
    referralPartnerId: number | null,
    dialog: DialogComponent,
    confirmTransfer: boolean,
  ) {
    const accountIds = this.getAccountIdsWithCurrentUser(franchisee);

    this.franchiseeService
      .assignFranchiseeAccount(franchisee.account_id, Number(this.column.account_id), {
        referral_partner_id: referralPartnerId,
        confirm_referral_partner_transfer: confirmTransfer || undefined,
      })
      .subscribe(
        response => {
          if (response?.requires_confirmation) {
            this.openTransferConfirm(dialog, response);
            return;
          }

          this.syncFranchiseeVisibleUsers(franchisee, accountIds);
          this.column.franchisee_id = franchisee.account_id;
          this.column.referral_partner_id = referralPartnerId;
          const dialogContent = dialog.component as FranchiseeSelectDialogComponent;
          this.column.referral_partner_name = referralPartnerId
            ? dialogContent?.referralPartners?.find(p => Number(p.id) === Number(referralPartnerId))?.name ||
              null
            : null;
          dialog.closeDialog();
          this.handleSuccess();
        },
        error => this.handleDialogError(dialog, error),
      );
  }

  private openTransferConfirm(dialog: DialogComponent, response: any) {
    const warning = (response.warnings && response.warnings[0]) || {};
    const partnerName = warning.source_referral_partner_name
      ? `"${warning.source_referral_partner_name}"`
      : 'the current referral partner';
    const message =
      warning.message ||
      `This account is linked to ${partnerName}. Changing the franchisee without selecting a referral partner will remove the referral relationship.`;

    this.store.dispatch(
      openDialog({
        dialogData: {
          content: message,
          title: 'Referral relationship will be lost',
          canClose: true,
          closeButtonText: 'Cancel',
          onClose: () => dialog.releaseButtons(),
          onCloseButton: confirmDialog => confirmDialog.closeDialog(),
          buttons: [
            {
              text: 'Continue',
              color: ButtonColor.ORANGE,
              size: ButtonSize.NORMAL,
              onClick: confirmDialog => {
                confirmDialog.closeDialog();
                this.saveAttribution(dialog, true);
              },
            },
          ],
        },
      }),
    );
  }

  private removeUserFromFranchisee(franchisee: IAccount, onSuccess: (accountIds: number[]) => void) {
    const accountIds = this.getAccountIdsWithoutCurrentUser(franchisee);

    this.franchiseeService
      .removeFranchiseeAssignment(franchisee.account_id, Number(this.column.account_id))
      .subscribe(
        () => onSuccess(accountIds),
        error => this.handleError(error),
      );
  }

  private findFranchisee(franchiseeId: number): IAccount | null {
    return this.franchisees.find(franchisee => franchisee.account_id === Number(franchiseeId)) || null;
  }

  private getAccountIdsWithCurrentUser(franchisee: IAccount): number[] {
    const accountIds = this.getAssignedAccountIds(franchisee);
    const currentAccountId = Number(this.column.account_id);

    if (accountIds.indexOf(currentAccountId) === -1) {
      accountIds.push(currentAccountId);
    }

    return accountIds;
  }

  private getAccountIdsWithoutCurrentUser(franchisee: IAccount): number[] {
    const currentAccountId = Number(this.column.account_id);
    return this.getAssignedAccountIds(franchisee).filter(accountId => accountId !== currentAccountId);
  }

  private getAssignedAccountIds(franchisee: IAccount): number[] {
    return (franchisee.visible_users || [])
      .map((user: IVisibleUsers) => Number(user.id))
      .filter(accountId => !!accountId);
  }

  private syncFranchiseeVisibleUsers(franchisee: IAccount, accountIds: number[]) {
    const visibleUsers = franchisee.visible_users || [];
    const normalizedAccountIds = accountIds.map(accountId => Number(accountId));

    franchisee.visible_users = visibleUsers.filter(
      user => normalizedAccountIds.indexOf(Number(user.id)) !== -1,
    );

    const currentAccountId = Number(this.column.account_id);

    if (
      normalizedAccountIds.indexOf(currentAccountId) !== -1 &&
      franchisee.visible_users.every(user => Number(user.id) !== currentAccountId)
    ) {
      franchisee.visible_users = [
        ...franchisee.visible_users,
        {
          id: this.column.account_id,
          email: this.column.email,
          first_name: this.column.first_name,
          last_name: this.column.last_name,
          account_role: this.column.account_role,
        },
      ];
    }
  }

  private handleSuccess() {
    this.tableComponent.refresh();
    this.store.dispatch(
      openSnackbar({
        snackbarData: {
          data: {
            message: 'Account settings successfully changed',
            type: 'success',
          },
        },
      }),
    );
  }

  private handleDialogError(dialog: DialogComponent, error) {
    dialog.releaseButtons();
    this.handleError(error);
  }

  private handleError(error) {
    this.tableComponent.refresh();
    this.store.dispatch(
      openSnackbar({
        snackbarData: {
          data: {
            message: error?.error || error,
            type: 'error',
          },
        },
      }),
    );
  }
}
